import SwitchInput from 'components/forms/inputs/rhf-inputs/SwitchInput.tsx';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput.tsx';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput.tsx';
import { ModalFooter } from 'components/modals/ModalFooter.tsx';
import { ModalHeader } from 'components/modals/ModalHeader.tsx';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useAddContactComplianceCheckMutation, useUpdateContactComplianceCheckMutation } from 'store/api/contacts.ts';
import { CheckStatusType, PartyComplianceCheckDto, PartyComplianceCheckForm } from 'types/compliance.ts';
import { ContactDto } from 'types/contacts.ts';
import { t } from 'utils/getTranslations.ts';
import { logError } from 'utils/monitoring';

interface ComplianceCheckModalProps {
    check?: PartyComplianceCheckDto;
    contact: ContactDto;
    refetchContact: () => Promise<unknown>;
    onClose: () => void;
    type: 'add' | 'edit';
}

const ComplianceCheckModal: React.FC<ComplianceCheckModalProps> = ({
    contact,
    check,
    onClose,
    refetchContact,
    type,
}) => {
    const [updateComplianceCheck] = useUpdateContactComplianceCheckMutation();
    const [addComplianceCheck] = useAddContactComplianceCheckMutation();

    const onSubmit = type === 'add' ? addComplianceCheck : updateComplianceCheck;

    const { handleSubmit, control } = useForm<PartyComplianceCheckForm>({
        defaultValues: {
            id: check?.id,
            complianceCheckName: check?.complianceCheckName,
            valueText: check?.valueText,
            status: check?.complianceCheckStatusTypeCode === CheckStatusType.PASSED,
        },
    });

    const onSave = () => {
        handleSubmit(async (data) => {
            const dto = mapToDto(data, contact.id);
            await onSubmit(dto)
                .unwrap()
                .then(async () => {
                    await refetchContact();
                })
                .then(() => {
                    onClose();
                })
                .catch((error) => {
                    console.error(error);
                    logError(error);
                });
        })();
    };

    return (
        <>
            <ModalHeader title={t(`compliance.additional-check.${type}`)} onCancel={onClose} />
            <div className="h-full overflow-y-auto px-6 py-3 max-sm:px-4 max-sm:py-6">
                <div className="flex flex-col gap-4">
                    <TextInput
                        control={control}
                        name="complianceCheckName"
                        label="compliance.check-description"
                        required
                        disabled={type === 'edit' && !check?.isManuallyEntered}
                    />
                    <SwitchInput control={control} name={'status'} label={'status.status'} />
                    <TextAreaInput
                        control={control}
                        name="valueText"
                        label="compliance.check-notes"
                        required
                        minRows={3}
                    />
                </div>
            </div>
            <ModalFooter onCancel={onClose} onSave={onSave} />
        </>
    );
};

export default ComplianceCheckModal;

const mapToDto = (data: PartyComplianceCheckForm, partyId: number) => {
    return {
        id: data.id,
        partyId: partyId,
        complianceCheckName: data.complianceCheckName,
        valueText: data.valueText,
        complianceCheckStatusTypeCode: data.status ? CheckStatusType.PASSED : CheckStatusType.ATTENTION_NEEDED,
    };
};
